import { demoFunc } from './dees-statsgrid.demo.js';
import * as plugins from '../../00plugins.js';
import {
  customElement,
  html,
  DeesElement,
  property,
  css,
  state,
  cssManager,
} from '@design.estate/dees-element';
import type { TemplateResult } from '@design.estate/dees-element';

import '../../00group-utility/dees-icon/dees-icon.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import '../../00group-button/dees-button/dees-button.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-layout/dees-tile/dees-tile.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-statsgrid': DeesStatsGrid;
  }
}

export interface ICpuCore {
  id: string | number;
  usage: number; // 0-100
  label?: string;
}

export interface IPartitionData {
  used: number;        // bytes
  total: number;       // bytes
  filesystem: string;  // e.g., 'ext4', 'NTFS', 'btrfs'
  mountPoint?: string; // e.g., '/', '/home', 'C:'
}

export interface IDiskData {
  capacity: number;    // bytes
  model?: string;      // e.g., 'Samsung 970 EVO Plus'
  type?: 'ssd' | 'hdd' | 'nvme';
  iops?: {
    read: number;
    write: number;
  };
  health?: number;     // 0-100 (100 = new, 0 = end of life)
}

export interface IStatsTile {
  id: string;
  title: string;
  value: number | string;
  unit?: string;
  type: 'number' | 'gauge' | 'percentage' | 'trend' | 'text' | 'multiPercentage' | 'cpuCores' | 'partition' | 'disk';

  // Layout options
  columnSpan?: number; // Number of columns to span (default: 1)

  // For gauge type
  gaugeOptions?: {
    min: number;
    max: number;
    thresholds?: Array<{value: number; color: string}>;
  };

  // For trend type
  trendData?: number[];

  // For multiPercentage type
  percentages?: Array<{
    label: string;
    value: number;
    color?: string;
  }>;

  // For cpuCores type
  coresData?: ICpuCore[];

  // For partition type
  partitionData?: IPartitionData;

  // For disk type
  diskData?: IDiskData;

  // Visual customization
  color?: string;
  icon?: string;
  description?: string;

  // Tile-specific actions
  actions?: plugins.tsclass.website.IMenuItem[];
}

@customElement('dees-statsgrid')
export class DeesStatsGrid extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View'];

  @property({ type: Array })
  accessor tiles: IStatsTile[] = [];

  @property({ type: Number })
  accessor minTileWidth: number = 250;

  @property({ type: Number })
  accessor gap: number = 16;

  @property({ type: Array })
  accessor gridActions: plugins.tsclass.website.IMenuItem[] = [];

  @state() private accessor availableWidth = 0;

  private get resolvedGap() { return Number.isFinite(this.gap) ? Math.max(0, this.gap) : 16; }
  private get resolvedMinWidth() { return Number.isFinite(this.minTileWidth) ? Math.max(1, this.minTileWidth) : 250; }
  private get columnCount() {
    return Math.max(1, Math.floor((this.availableWidth + this.resolvedGap) / (this.resolvedMinWidth + this.resolvedGap)));
  }
  private tileSpan(tile: IStatsTile) {
    return Math.min(this.columnCount, Number.isFinite(tile.columnSpan) ? Math.max(1, Math.floor(tile.columnSpan!)) : 1);
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block; width: 100%; min-width: 0; font-family: var(--dees-font-family);
        --value-font-size: 28px; --unit-font-size: 13px; --label-font-size: 12px;
      }
      .grid-header { display: flex; justify-content: flex-end; margin-bottom: 12px; }
      .grid-actions { display: flex; flex-wrap: wrap; gap: 8px; }
      .stats-grid { display: grid; width: 100%; min-width: 0; align-items: stretch; }
      .stats-tile { min-width: 0; }
      .stats-tile::part(header) { display: none; }
      .stats-tile::part(content) { border: 0; display: flex; flex-direction: column; }
      .stats-tile::part(outer) { border-color: var(--dees-color-border-subtle); }
      .stats-tile.clickable { cursor: pointer; }
      .tile-body { padding: 16px; display: flex; flex: 1; flex-direction: column; gap: 12px; min-height: 148px; box-sizing: border-box; }
      .tile-header { display: flex; align-items: center; gap: 8px; min-height: 28px; }
      .tile-title { flex: 1; min-width: 0; margin: 0; color: var(--dees-color-text-secondary); font-size: 13px; line-height: 1.4; font-weight: 500; overflow-wrap: anywhere; }
      .tile-icon { color: var(--dees-color-text-muted); flex: 0 0 auto; }
      .tile-action {
        display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto;
        box-sizing: border-box; width: 28px; height: 28px; padding: 0; border: 0;
        border-radius: var(--dees-radius-md); color: var(--dees-color-text-secondary); background: transparent; cursor: pointer;
      }
      .tile-action:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }
      .tile-action:active { background: var(--dees-color-active); }
      @media (hover: hover) {
        .stats-tile.clickable:hover::part(outer) { border-color: var(--dees-color-border-strong); }
        .tile-action:hover { background: var(--dees-color-hover); color: var(--dees-color-text-primary); }
      }
      @media (pointer: coarse) { .tile-action { width: 44px; height: 44px; } }
      .tile-content { display: flex; flex-direction: column; flex: 1; min-width: 0; min-height: 40px; }
      .tile-value { font-size: var(--value-font-size); font-weight: 600; color: var(--dees-color-text-primary); line-height: 1.15; display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px; letter-spacing: -.025em; }
      .tile-unit { font-size: var(--unit-font-size); font-weight: 400; color: var(--dees-color-text-secondary); }
      .tile-description { margin-top: auto; font-size: var(--label-font-size); line-height: 1.5; color: var(--dees-color-text-muted); overflow-wrap: anywhere; }
      .tile-value, .percentage-value, .trend-value, .cpu-cores-value, .partition-percentage, .disk-capacity { font-variant-numeric: tabular-nums; }
      .text-value, .tile-value, .trend-header, .partition-meta, .disk-model { overflow-wrap: anywhere; min-width: 0; }
      .empty { padding: 32px 16px; text-align: center; color: var(--dees-color-text-muted); font-size: 13px; }
      /* Gauge Styles */
      .gauge-wrapper {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        justify-content: space-between;
        align-items: flex-start;
        flex: 1;
      }

      .gauge-container {
        width: 76px;
        max-width: 100%;
        height: 46px;
        align-self: flex-end;
        margin-left: auto;
        position: relative;
        margin-top: 0;
      }

      .gauge-svg {
        width: 100%;
        height: 100%;
      }

      .gauge-background {
        fill: none;
        stroke: var(--dees-color-border-default);
        stroke-width: 6;
      }

      .gauge-fill {
        fill: none;
        stroke-width: 6;
        stroke-linecap: round;
        transition: stroke-dashoffset 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      }

      /* Percentage Styles */
      .percentage-wrapper {
        width: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      .percentage-value {
        font-size: var(--value-font-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.1;
        letter-spacing: -0.025em;
        margin-bottom: 8px;
      }

      .percentage-bar {
        width: 100%;
        height: 6px;
        background: var(--dees-color-border-default);
        border-radius: 3px;
        overflow: hidden;
        margin-top: auto;
      }

      .percentage-fill {
        height: 100%;
        background: var(--dees-color-text-muted);
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: 3px;
      }

      /* Multi Percentage Styles */
      .multi-percentage-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      .multi-percentage-items {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: auto;
      }

      .multi-percentage-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .multi-percentage-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }

      .multi-percentage-label {
        font-size: 11px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
      }

      .multi-percentage-value {
        font-size: 13px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
        letter-spacing: -0.01em;
      }

      .multi-percentage-bar {
        width: 100%;
        height: 4px;
        background: var(--dees-color-border-default);
        border-radius: 2px;
        overflow: hidden;
      }

      .multi-percentage-fill {
        height: 100%;
        background: var(--dees-color-text-muted);
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: 2px;
      }

      /* CPU Cores Styles */
      .cpu-cores-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 80px;
      }

      .cpu-cores-header {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-bottom: 12px;
      }

      .cpu-cores-value {
        font-size: var(--value-font-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.1;
        letter-spacing: -0.025em;
      }

      .cpu-cores-unit {
        font-size: var(--unit-font-size);
        font-weight: 400;
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
      }

      .cpu-cores-label {
        font-size: var(--label-font-size);
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
        margin-left: auto;
      }

      .cpu-cores-bars {
        display: flex;
        align-items: flex-end;
        gap: 3px;
        flex: 1;
        overflow-x: auto;
        min-height: 60px;
        padding: 4px 0;
      }

      .cpu-cores-bars.centered {
        justify-content: center;
      }

      .cpu-core-bar-container {
        flex: 1;
        min-width: 6px;
        max-width: 24px;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }

      .cpu-core-bar-wrapper {
        flex: 1;
        width: 100%;
        background: var(--dees-color-border-default);
        border-radius: 2px;
        position: relative;
        overflow: hidden;
        min-height: 40px;
      }

      .cpu-core-bar-fill {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        background: var(--dees-color-text-muted);
        transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease;
        border-radius: 2px 2px 0 0;
      }

      .cpu-core-bar-fill.low {
        background: var(--dees-color-accent-success);
      }

      .cpu-core-bar-fill.medium {
        background: var(--dees-color-accent-warning);
      }

      .cpu-core-bar-fill.high {
        background: var(--dees-color-accent-error);
      }

      .cpu-core-label {
        font-size: 9px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      /* Partition Styles */
      .partition-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 8px;
      }

      .partition-header {
        display: flex;
        align-items: baseline;
        gap: 8px;
      }

      .partition-percentage {
        font-size: var(--value-font-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.1;
        letter-spacing: -0.025em;
      }

      .partition-bar {
        width: 100%;
        height: 6px;
        background: var(--dees-color-border-default);
        border-radius: 3px;
        overflow: hidden;
      }

      .partition-bar-fill {
        height: 100%;
        background: var(--dees-color-text-muted);
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: 3px;
      }

      .partition-bar-fill.warning {
        background: var(--dees-color-accent-warning);
      }

      .partition-bar-fill.critical {
        background: var(--dees-color-accent-error);
      }

      .partition-stats {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: auto;
      }

      .partition-stat {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .partition-stat-label {
        font-size: 10px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.02em;
      }

      .partition-stat-value {
        font-size: 13px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
        letter-spacing: -0.01em;
      }

      .partition-meta {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
      }

      .partition-filesystem {
        font-size: 11px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        background: var(--dees-color-badge-default-bg);
        padding: 2px 6px;
        border-radius: 3px;
      }

      .partition-mountpoint {
        font-size: 11px;
        color: var(--dees-color-text-secondary);
      }

      /* Disk Styles */
      .disk-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 8px;
      }

      .disk-capacity {
        font-size: var(--value-font-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.1;
        letter-spacing: -0.025em;
      }

      .disk-model {
        font-size: 12px;
        color: var(--dees-color-text-secondary);
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .disk-type-badge {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        color: var(--dees-color-text-secondary);
        background: var(--dees-color-badge-default-bg);
        padding: 2px 6px;
        border-radius: 3px;
      }

      .disk-metrics {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: auto;
      }

      .disk-iops {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .disk-iops-item {
        display: flex;
        align-items: baseline;
        gap: 4px;
      }

      .disk-iops-label {
        font-size: 10px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        text-transform: uppercase;
      }

      .disk-iops-value {
        font-size: 13px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
      }

      .disk-health {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .disk-health-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }

      .disk-health-label {
        font-size: 10px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        text-transform: uppercase;
      }

      .disk-health-value {
        font-size: 12px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
      }

      .disk-health-bar {
        width: 100%;
        height: 4px;
        background: var(--dees-color-border-default);
        border-radius: 2px;
        overflow: hidden;
      }

      .disk-health-fill {
        height: 100%;
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: 2px;
      }

      .disk-health-fill.good {
        background: var(--dees-color-text-success);
      }

      .disk-health-fill.warning {
        background: var(--dees-color-accent-warning);
      }

      .disk-health-fill.critical {
        background: var(--dees-color-accent-error);
      }

      /* Trend Styles */
      .trend-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
      }

      .trend-header {
        display: flex;
        align-items: baseline;
        gap: 8px;
      }

      .trend-value {
        font-size: var(--value-font-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.1;
        letter-spacing: -0.025em;
      }

      .trend-unit {
        font-size: var(--unit-font-size);
        font-weight: 400;
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
      }

      .trend-label {
        font-size: var(--label-font-size);
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
        margin-left: auto;
      }

      .trend-graph {
        width: 100%;
        height: 28px;
        position: relative;
        margin-top: auto;
      }

      .trend-svg {
        width: 100%;
        height: 100%;
        display: block;
      }

      .trend-line {
        fill: none;
        stroke: var(--dees-color-text-muted);
        stroke-width: 1.5;
        stroke-linejoin: round;
        stroke-linecap: round;
      }

      .trend-area {
        fill: color-mix(in srgb, var(--dees-color-text-muted) 10%, transparent);
      }

      /* Text Value Styles */
      .text-value {
        font-size: var(--value-font-size);
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.1;
        letter-spacing: -0.025em;
      }
    `,
    css`
      @media (prefers-reduced-motion: reduce) {
        .gauge-fill, .percentage-fill, .multi-percentage-fill, .cpu-core-bar-fill, .partition-bar-fill, .disk-health-fill { transition: none; }
      }
    `,
  ];

  constructor() {
    super();
    this.registerStartupFunction((signal) => {
      const observer = new ResizeObserver(([entry]) => {
        if (!signal.aborted) this.availableWidth = entry.contentRect.width;
      });
      observer.observe(this);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  public render(): TemplateResult {
    return html`
      ${this.gridActions.length > 0 ? html`
        <div class="grid-header">
          <div class="grid-actions">
            ${this.gridActions.map(action => html`
              <dees-button
                @clicked=${() => this.handleGridAction(action)}
                type="outline"
                size="sm"
                .text=${action.name}
                .icon=${action.iconName || ''}
              ></dees-button>
            `)}
          </div>
        </div>
      ` : ''}
      
      <div class="stats-grid" style=${`grid-template-columns: repeat(${Math.min(this.columnCount, Math.max(1, this.tiles.reduce((count, tile) => count + this.tileSpan(tile), 0)))}, minmax(0, 1fr)); gap: ${this.resolvedGap}px;`}>
        ${this.tiles.map(tile => this.renderTile(tile))}
      </div>
      ${!this.tiles.length ? html`<div class="empty">No metrics available yet.</div>` : ''}
    `;
  }

  private renderTile(tile: IStatsTile): TemplateResult {
    const hasActions = tile.actions && tile.actions.length > 0;
    const clickable = hasActions && tile.actions!.length === 1;
    return html`
      <dees-tile class="stats-tile ${clickable ? 'clickable' : ''}" style=${`grid-column: span ${this.tileSpan(tile)}`}
        @click=${clickable ? (event: MouseEvent) => {
          if (event.composedPath().some((node) => node instanceof HTMLElement && (['BUTTON', 'A', 'INPUT'].includes(node.tagName) || node.isContentEditable))) return;
          void this.handleTileAction(tile.actions![0], tile);
        } : undefined}
        @contextmenu=${hasActions ? (event: MouseEvent) => this.showContextMenu(event, tile) : undefined}>
        <div class="tile-body">
          <div class="tile-header">
            ${tile.icon ? html`<dees-icon class="tile-icon" aria-hidden="true" .icon=${tile.icon} iconSize="16"></dees-icon>` : ''}
            <h3 class="tile-title">${tile.title}</h3>
            ${hasActions ? html`<button type="button" class="tile-action"
              aria-label=${clickable ? tile.actions![0].name : `Actions for ${tile.title}`}
              aria-haspopup=${clickable ? 'false' : 'menu'}
              @click=${(event: MouseEvent) => {
                event.stopPropagation();
                if (clickable) void this.handleTileAction(tile.actions![0], tile);
                else this.openActionMenu(event);
              }}
              @keydown=${(event: KeyboardEvent) => {
                if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) this.openActionMenu(event);
              }}>
              <dees-icon aria-hidden="true" .icon=${clickable ? 'lucide:ArrowUpRight' : 'lucide:Ellipsis'} iconSize="16"></dees-icon>
            </button>` : ''}
          </div>
          <div class="tile-content">${this.renderTileContent(tile)}</div>
          ${tile.description ? html`<div class="tile-description">${tile.description}</div>` : ''}
        </div>
      </dees-tile>`;
  }

  private openActionMenu(event: MouseEvent | KeyboardEvent) {
    event.preventDefault(); event.stopPropagation();
    const control = event.currentTarget as HTMLElement;
    control.focus({ preventScroll: true });
    const rect = control.getBoundingClientRect();
    control.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, composed: true, cancelable: true, clientX: rect.left, clientY: rect.bottom }));
  }

  private renderTileContent(tile: IStatsTile): TemplateResult {
    switch (tile.type) {
      case 'number':
        return html`
          <div class="tile-value" style="${tile.color ? `color: ${tile.color}` : ''}">
            <span>${tile.value}</span>
            ${tile.unit ? html`<span class="tile-unit">${tile.unit}</span>` : ''}
          </div>
        `;

      case 'gauge':
        return this.renderGauge(tile);

      case 'percentage':
        return this.renderPercentage(tile);

      case 'trend':
        return this.renderTrend(tile);

      case 'multiPercentage':
        return this.renderMultiPercentage(tile);

      case 'cpuCores':
        return this.renderCpuCores(tile);

      case 'partition':
        return this.renderPartition(tile);

      case 'disk':
        return this.renderDisk(tile);

      case 'text':
        return html`
          <div class="text-value" style="${tile.color ? `color: ${tile.color}` : ''}">
            ${tile.value}
          </div>
        `;

      default:
        return html`<div class="tile-value">${tile.value}</div>`;
    }
  }

  private renderGauge(tile: IStatsTile): TemplateResult {
    const value = typeof tile.value === 'number' ? tile.value : parseFloat(tile.value);
    const options = tile.gaugeOptions || { min: 0, max: 100 };
    const percentage = ((value - options.min) / (options.max - options.min)) * 100;

    // SVG dimensions and calculations
    const width = 120;
    const height = 70;
    const strokeWidth = 6;
    const padding = strokeWidth / 2 + 2;
    const radius = 40;
    const centerX = width / 2;
    const centerY = height - padding;
    
    // Arc path
    const startX = centerX - radius;
    const startY = centerY;
    const endX = centerX + radius;
    const endY = centerY;
    const arcPath = `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;
    
    // Calculate stroke dasharray and dashoffset
    const circumference = Math.PI * radius;
    const strokeDashoffset = circumference - (circumference * percentage) / 100;

    let strokeColor = tile.color || 'var(--dees-color-text-secondary)';
    if (options.thresholds) {
      const sortedThresholds = [...options.thresholds].sort((a, b) => b.value - a.value);
      for (const threshold of sortedThresholds) {
        if (value >= threshold.value) {
          strokeColor = threshold.color;
          break;
        }
      }
    }

    return html`
      <div class="gauge-wrapper">
        <div class="tile-value">${value}${tile.unit ? html`<span class="tile-unit gauge-unit">${tile.unit}</span>` : ''}</div>
        <div class="gauge-container">
          <svg aria-hidden="true" class="gauge-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet">
            <!-- Background arc -->
            <path
              class="gauge-background"
              d="${arcPath}"
            />
            <!-- Filled arc -->
            <path
              class="gauge-fill"
              d="${arcPath}"
              style="stroke: ${strokeColor}"
              stroke-dasharray="${circumference}"
              stroke-dashoffset="${strokeDashoffset}"
            />
          </svg>
        </div>
      </div>
    `;
  }

  private renderPercentage(tile: IStatsTile): TemplateResult {
    const value = typeof tile.value === 'number' ? tile.value : parseFloat(tile.value);
    const percentage = Math.min(100, Math.max(0, value));

    return html`
      <div class="percentage-wrapper">
        <div class="percentage-value">${percentage}%</div>
        <div class="percentage-bar">
          <div 
            class="percentage-fill" 
            style="width: ${percentage}%; ${tile.color ? `background: ${tile.color}` : ''}"
          ></div>
        </div>
      </div>
    `;
  }

  private renderTrend(tile: IStatsTile): TemplateResult {
    if (!tile.trendData || tile.trendData.length < 2) {
      return html`<div class="tile-value">${tile.value}</div>`;
    }

    const data = tile.trendData;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 300;
    const height = 32;
    
    // Add padding to prevent clipping
    const padding = 2;
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width;
      const y = padding + (height - 2 * padding) - ((value - min) / range) * (height - 2 * padding);
      return `${x},${y}`;
    }).join(' ');

    const areaPoints = `0,${height} ${points} ${width},${height}`;

    return html`
      <div class="trend-container">
        <div class="trend-header">
          <span class="trend-value">${tile.value}</span>
          ${tile.unit ? html`<span class="trend-unit">${tile.unit}</span>` : ''}
        </div>
        <div class="trend-graph">
          <svg aria-hidden="true" class="trend-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">
            <polygon class="trend-area" points="${areaPoints}" />
            <polyline class="trend-line" points="${points}" />
          </svg>
        </div>
      </div>
    `;
  }

  private renderMultiPercentage(tile: IStatsTile): TemplateResult {
    if (!tile.percentages || tile.percentages.length === 0) {
      return html`<div class="tile-value">${tile.value}</div>`;
    }

    // Limit to 3 percentages
    const items = tile.percentages.slice(0, 3);

    return html`
      <div class="multi-percentage-wrapper">
        <div class="multi-percentage-items">
          ${items.map(item => {
            const percentage = Math.min(100, Math.max(0, item.value));
            return html`
              <div class="multi-percentage-item">
                <div class="multi-percentage-header">
                  <span class="multi-percentage-label">${item.label}</span>
                  <span class="multi-percentage-value">${percentage}%</span>
                </div>
                <div class="multi-percentage-bar">
                  <div
                    class="multi-percentage-fill"
                    style="width: ${percentage}%; ${item.color ? `background: ${item.color}` : ''}"
                  ></div>
                </div>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  private renderCpuCores(tile: IStatsTile): TemplateResult {
    if (!tile.coresData || tile.coresData.length === 0) {
      return html`<div class="tile-value">${tile.value}</div>`;
    }

    const cores = tile.coresData;
    const avgUsage = Math.round(cores.reduce((sum, core) => sum + core.usage, 0) / cores.length);

    // Determine color class based on usage
    const getColorClass = (usage: number): string => {
      if (usage < 50) return 'low';
      if (usage < 80) return 'medium';
      return 'high';
    };

    // Calculate if bars should be centered (when they take up less than 66.6% of available width)
    // Max bar width = cores * 24px + (cores - 1) * 3px gap
    const maxBarsWidth = cores.length * 24 + (cores.length - 1) * 3;
    // Estimate tile content width based on columnSpan and minTileWidth (subtract padding)
    const columnSpan = tile.columnSpan || 1;
    const estimatedTileWidth = (this.minTileWidth * columnSpan) + ((columnSpan - 1) * this.gap) - 32; // 32px for padding
    const shouldCenter = maxBarsWidth < estimatedTileWidth * 0.666;

    return html`
      <div class="cpu-cores-wrapper">
        <div class="cpu-cores-header">
          <span class="cpu-cores-value">${avgUsage}</span>
          <span class="cpu-cores-unit">%</span>
          <span class="cpu-cores-label">${cores.length} cores</span>
        </div>
        <div class="cpu-cores-bars ${shouldCenter ? 'centered' : ''}">
          ${cores.map(core => {
            const usage = Math.min(100, Math.max(0, core.usage));
            const colorClass = getColorClass(usage);
            return html`
              <div class="cpu-core-bar-container" title="Core ${core.label || core.id}: ${usage}%">
                <div class="cpu-core-bar-wrapper">
                  <div
                    class="cpu-core-bar-fill ${colorClass}"
                    style="height: ${usage}%"
                  ></div>
                </div>
                ${cores.length <= 16 ? html`
                  <span class="cpu-core-label">${core.label || core.id}</span>
                ` : ''}
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  private renderPartition(tile: IStatsTile): TemplateResult {
    if (!tile.partitionData) {
      return html`<div class="tile-value">${tile.value}</div>`;
    }

    const { used, total, filesystem, mountPoint } = tile.partitionData;
    const percentage = Math.min(100, Math.max(0, (used / total) * 100));
    const free = total - used;

    // Determine color class based on usage
    const getColorClass = (): string => {
      if (percentage >= 90) return 'critical';
      if (percentage >= 75) return 'warning';
      return '';
    };

    return html`
      <div class="partition-wrapper">
        <div class="partition-header">
          <span class="partition-percentage">${Math.round(percentage)}%</span>
        </div>
        <div class="partition-bar">
          <div
            class="partition-bar-fill ${getColorClass()}"
            style="width: ${percentage}%"
          ></div>
        </div>
        <div class="partition-stats">
          <div class="partition-stat">
            <span class="partition-stat-label">Used</span>
            <span class="partition-stat-value">${this.formatBytes(used)}</span>
          </div>
          <div class="partition-stat">
            <span class="partition-stat-label">Free</span>
            <span class="partition-stat-value">${this.formatBytes(free)}</span>
          </div>
        </div>
        <div class="partition-meta">
          <span class="partition-filesystem">${filesystem}</span>
          ${mountPoint ? html`<span class="partition-mountpoint">${mountPoint}</span>` : ''}
        </div>
      </div>
    `;
  }

  private renderDisk(tile: IStatsTile): TemplateResult {
    if (!tile.diskData) {
      return html`<div class="tile-value">${tile.value}</div>`;
    }

    const { capacity, model, type, iops, health } = tile.diskData;

    // Determine health color class (inverted - high is good)
    const getHealthClass = (value: number): string => {
      if (value >= 70) return 'good';
      if (value >= 30) return 'warning';
      return 'critical';
    };

    return html`
      <div class="disk-wrapper">
        <div class="disk-capacity">${this.formatBytes(capacity)}</div>
        ${model || type ? html`
          <div class="disk-model">
            ${model ? html`<span>${model}</span>` : ''}
            ${type ? html`<span class="disk-type-badge">${type}</span>` : ''}
          </div>
        ` : ''}
        <div class="disk-metrics">
          ${iops ? html`
            <div class="disk-iops">
              <div class="disk-iops-item">
                <span class="disk-iops-label">Read</span>
                <span class="disk-iops-value">${iops.read.toLocaleString()}</span>
              </div>
              <div class="disk-iops-item">
                <span class="disk-iops-label">Write</span>
                <span class="disk-iops-value">${iops.write.toLocaleString()}</span>
              </div>
            </div>
          ` : ''}
          ${health !== undefined ? html`
            <div class="disk-health">
              <div class="disk-health-header">
                <span class="disk-health-label">Health</span>
                <span class="disk-health-value">${health}%</span>
              </div>
              <div class="disk-health-bar">
                <div
                  class="disk-health-fill ${getHealthClass(health)}"
                  style="width: ${health}%"
                ></div>
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private async handleGridAction(action: plugins.tsclass.website.IMenuItem) {
    if (action.action) {
      await action.action();
    }
  }

  private async handleTileAction(action: plugins.tsclass.website.IMenuItem, _tile: IStatsTile) {
    if (action.action) {
      await action.action();
    }
    // Note: tile data is available through closure when defining actions
  }

  private showContextMenu(event: MouseEvent, tile: IStatsTile) {
    if (!tile.actions || tile.actions.length === 0) return;
    // Delegate to the canonical overlay API: it creates a window layer,
    // positions with viewport edge-detection, and dismisses on outside click.
    // (It calls stopPropagation, so the global contextmenu listener won't also
    // open an empty menu.)
    DeesContextmenu.openContextMenuWithOptions(event, tile.actions);
  }
}
